import { useMemo, useState } from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { ChevronDown, Map as MapIcon, SlidersHorizontal, X } from "lucide-react"
import { MapCanvas, type MapFeatureProperties } from "@/components/MapCanvas"
import { PublicShell } from "@/components/PublicShell"
import { captureAnalyticsEvent } from "@/lib/analytics"
import { localePathsFrom, type AlternateLink, type PublicLocale } from "@/lib/public-links"
import type { PageProps } from "@/types/inertia"

type Destination = {
  id: number
  name: string
  path: string
  latitude: number
  longitude: number
}

type Category = {
  id: number
  key: string
  label: string
  icon: string
  map_color: string
}

type Filter = { key: string; label: string }

const COPY = {
  en: {
    title: "Explore the map",
    intro: "Move around the map to load published places in the visible area.",
    filters: "Map filters",
    categories: "Categories",
    trails: "Trails",
    attributes: "Place details",
    close: "Close place card",
    details: "View place details",
    noFacts: "Open the place page for practical details.",
  },
  de: {
    title: "Karte erkunden",
    intro: "Bewege die Karte, um veröffentlichte Orte im sichtbaren Bereich zu laden.",
    filters: "Kartenfilter",
    categories: "Kategorien",
    trails: "Wege",
    attributes: "Ortsmerkmale",
    close: "Ortskarte schließen",
    details: "Ortsdetails ansehen",
    noFacts: "Öffne die Ortsseite für praktische Angaben.",
  },
} satisfies Record<PublicLocale, Record<string, string>>

export default function MapShow({
  locale,
  destination,
  categories,
  filters,
  data_url,
  canonical_url,
  alternate_links,
}: {
  locale: PublicLocale
  destination: Destination
  categories: Category[]
  filters: Filter[]
  data_url: string
  canonical_url: string
  alternate_links: AlternateLink[]
}) {
  const copy = COPY[locale]
  const { props } = usePage<PageProps>()
  const [selectedCategories, setSelectedCategories] = useState(() => new Set(categories.map((category) => category.key)))
  const [selectedFilters, setSelectedFilters] = useState(() => new Set<string>())
  const [showTrails, setShowTrails] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState<MapFeatureProperties | null>(null)

  const dataUrl = useMemo(() => {
    const params = new URLSearchParams()
    if (selectedCategories.size !== categories.length) {
      params.set("categories", selectedCategories.size > 0 ? Array.from(selectedCategories).join(",") : "none")
    }
    selectedFilters.forEach((filter) => params.set(filter, "1"))
    if (showTrails) params.set("trails", "1")
    const query = params.toString()
    return query ? `${data_url}${data_url.includes("?") ? "&" : "?"}${query}` : data_url
  }, [ categories.length, data_url, selectedCategories, selectedFilters, showTrails ])

  const toggleCategory = (key: string) => {
    setSelectedPlace(null)
    captureAnalyticsEvent("map_filter_toggled", { filter_type: "category", filter: key, active_after_click: !selectedCategories.has(key) })
    setSelectedCategories((current) => toggledSet(current, key))
  }
  const toggleFilter = (key: string) => {
    setSelectedPlace(null)
    captureAnalyticsEvent("map_filter_toggled", { filter_type: "attribute", filter: key, active_after_click: !selectedFilters.has(key) })
    setSelectedFilters((current) => toggledSet(current, key))
  }
  const toggleTrails = () => {
    setSelectedPlace(null)
    captureAnalyticsEvent("map_filter_toggled", { filter_type: "content", filter: "trails", active_after_click: !showTrails })
    setShowTrails((value) => !value)
  }
  const selectPlace = (place: MapFeatureProperties) => {
    setSelectedPlace(place)
    captureAnalyticsEvent("map_marker_selected", { place_id: place.id, feature_kind: place.feature_kind })
  }

  return (
    <>
      <Head title={`${copy.title} · ${destination.name}`}>
        <meta name="description" content={`${copy.intro} ${destination.name}.`} />
        <link rel="canonical" href={canonical_url} />
        {alternate_links.map((link) => (
          <link key={link.href_lang} rel="alternate" hrefLang={link.href_lang} href={link.href} />
        ))}
      </Head>
      <PublicShell locale={locale} localePaths={localePathsFrom(alternate_links)} hideFooter>
        <main className="relative h-[calc(100dvh-3.875rem)] min-h-[32rem] overflow-hidden bg-surface-subtle">
          <MapCanvas
            maptilerKey={props.maptiler_key}
            center={[ destination.longitude, destination.latitude ]}
            zoom={10}
            dataUrl={dataUrl}
            onFeatureSelect={selectPlace}
            className="h-full w-full rounded-none border-0"
          />

          <section className="absolute top-3 left-3 z-10 max-w-[calc(100%-1.5rem)] rounded-xl border border-hairline bg-surface/95 p-4 shadow-lg backdrop-blur md:top-5 md:left-5 md:max-w-md">
            <Link href={destination.path} className="public-kicker no-underline">{destination.name}</Link>
            <h1 className="public-card-title mt-1 flex items-center gap-2"><MapIcon className="h-5 w-5 text-accent" />{copy.title}</h1>
            <p className="mt-1 text-sm text-ink-muted">{copy.intro}</p>
          </section>

          <aside className="absolute top-5 right-5 z-10 hidden max-h-[calc(100%-2.5rem)] w-72 overflow-y-auto rounded-xl border border-hairline bg-surface/95 p-4 shadow-lg backdrop-blur md:block">
            <h2 className="public-card-title flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" />{copy.filters}</h2>
            <FilterControls
              copy={copy}
              categories={categories}
              filters={filters}
              selectedCategories={selectedCategories}
              selectedFilters={selectedFilters}
              showTrails={showTrails}
              toggleCategory={toggleCategory}
              toggleFilter={toggleFilter}
              toggleTrails={toggleTrails}
            />
          </aside>

          <details className="group absolute right-3 bottom-3 left-3 z-20 rounded-xl border border-hairline bg-surface/95 shadow-lg backdrop-blur md:hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between p-4 font-semibold">
              <span className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" />{copy.filters}</span>
              <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
            </summary>
            <div className="max-h-[55dvh] overflow-y-auto border-t border-hairline px-4 pb-4">
              <FilterControls
                copy={copy}
                categories={categories}
                filters={filters}
                selectedCategories={selectedCategories}
                selectedFilters={selectedFilters}
                showTrails={showTrails}
                toggleCategory={toggleCategory}
                toggleFilter={toggleFilter}
                toggleTrails={toggleTrails}
              />
            </div>
          </details>

          {selectedPlace && (
            <PlaceCard place={selectedPlace} copy={copy} onClose={() => setSelectedPlace(null)} />
          )}
        </main>
      </PublicShell>
    </>
  )
}

function FilterControls({
  copy,
  categories,
  filters,
  selectedCategories,
  selectedFilters,
  showTrails,
  toggleCategory,
  toggleFilter,
  toggleTrails,
}: {
  copy: (typeof COPY)[PublicLocale]
  categories: Category[]
  filters: Filter[]
  selectedCategories: Set<string>
  selectedFilters: Set<string>
  showTrails: boolean
  toggleCategory: (key: string) => void
  toggleFilter: (key: string) => void
  toggleTrails: () => void
}) {
  return (
    <div className="mt-3 space-y-5">
      <fieldset>
        <legend className="text-xs font-bold tracking-wide text-ink-muted uppercase">{copy.categories}</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {categories.map((category) => (
            <Toggle key={category.key} active={selectedCategories.has(category.key)} onClick={() => toggleCategory(category.key)}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: category.map_color }} />{category.label}
            </Toggle>
          ))}
          <Toggle active={showTrails} onClick={toggleTrails}>{copy.trails}</Toggle>
        </div>
      </fieldset>
      <fieldset>
        <legend className="text-xs font-bold tracking-wide text-ink-muted uppercase">{copy.attributes}</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {filters.map((filter) => (
            <Toggle key={filter.key} active={selectedFilters.has(filter.key)} onClick={() => toggleFilter(filter.key)}>{filter.label}</Toggle>
          ))}
        </div>
      </fieldset>
    </div>
  )
}

function Toggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={active
        ? "inline-flex items-center gap-1.5 rounded-full border border-accent bg-accent px-3 py-1.5 text-xs font-semibold text-white"
        : "inline-flex items-center gap-1.5 rounded-full border border-hairline-strong bg-surface px-3 py-1.5 text-xs font-semibold text-ink-secondary"}
    >
      {children}
    </button>
  )
}

function PlaceCard({ place, copy, onClose }: { place: MapFeatureProperties; copy: (typeof COPY)[PublicLocale]; onClose: () => void }) {
  const facts: string[] = place.key_facts ? JSON.parse(place.key_facts) : []
  return (
    <article className="absolute right-3 bottom-20 left-3 z-30 overflow-hidden rounded-xl border border-hairline bg-surface shadow-xl md:right-auto md:bottom-5 md:left-5 md:w-96">
      <button type="button" onClick={onClose} aria-label={copy.close} className="absolute top-2 right-2 z-10 rounded-full bg-surface/90 p-2 text-ink-display shadow"><X className="h-4 w-4" /></button>
      {place.photo_url && <img src={place.photo_url} alt={place.photo_alt ?? ""} className="h-32 w-full object-cover" />}
      <div className="p-4">
        <p className="public-kicker">{place.category_label}</p>
        <h2 className="public-card-title mt-1">{place.name}</h2>
        {place.summary && <p className="mt-2 text-sm text-ink-body">{place.summary}</p>}
        <ul className="mt-3 flex flex-wrap gap-1.5 text-xs text-ink-muted">
          {(facts.length > 0 ? facts : [ copy.noFacts ]).map((fact) => <li key={fact} className="rounded-full bg-surface-subtle px-2.5 py-1">{fact}</li>)}
        </ul>
        <Link href={place.path} onClick={() => captureAnalyticsEvent("place_card_clicked", { place_id: place.id, source: "explore_map" })} className="btn btn-primary mt-4 w-full">{copy.details}</Link>
      </div>
    </article>
  )
}

function toggledSet(current: Set<string>, key: string) {
  const next = new Set(current)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}
